import { useState, useCallback, useMemo, createContext, useContext } from 'react';

// Create context for shared message status state
const MessageStatusContext = createContext();

/**
 * Provider component for unified message status management
 * This consolidates all the scattered loading states into a single, clean system
 * 
 * NOTE: SEARCHING status is NOT handled here - ChatMessage handles SearchIndicator directly
 * because it needs the full message object and sophisticated state management for favicon animations
 */
export const MessageStatusProvider = ({ children }) => {
  // SHARED state across all components that use useMessageStatus
  const [messageStatuses, setMessageStatuses] = useState(new Map());

  // Status types enum for consistency - memoized to prevent dependency changes
  const STATUS_TYPES = useMemo(() => ({
    THINKING: 'thinking',
    CLAUDE_THINKING: 'claude_thinking',
    SEARCHING: 'searching', // Still available for status tracking, but not for indicators
    ANALYZING: 'analyzing',
    DEEP_RESEARCH: 'deep_research',
    GENERATING_IMAGE: 'generating_image',
    GENERATING_FILE: 'generating_file',
    SUPERBUCKET_QUERY: 'superbucket_query',
    HUBSPOT_QUERY: 'hubspot_query',
    STREAMING: 'streaming',
    COMPLETE: 'complete'
  }), []);

  // Update message status
  const updateMessageStatus = useCallback((messageId, statusData) => {

    
    setMessageStatuses(prev => {
      const newMap = new Map(prev);
      const existingStatus = prev.get(messageId) || { type: null, data: {} };
      
      // 🧠 WARN: If overwriting thinking status with something else
      if (existingStatus.type === STATUS_TYPES.CLAUDE_THINKING && statusData.type !== STATUS_TYPES.CLAUDE_THINKING) {
        console.warn('🧠 WARNING: Overwriting thinking status with:', statusData.type);
      }
      
      const newStatus = {
        ...existingStatus,
        ...statusData,
        lastUpdated: Date.now()
      };
      newMap.set(messageId, newStatus);
      return newMap;
    });
  }, [STATUS_TYPES]);

  // Get message status
  const getMessageStatus = useCallback((messageId) => {
    const status = messageStatuses.get(messageId) || { type: null, data: {} };
    return status;
  }, [messageStatuses]);

  // Clear message status
  const clearMessageStatus = useCallback((messageId) => {
    setMessageStatuses(prev => {
      const newMap = new Map(prev);
      newMap.delete(messageId);
      return newMap;
    });
  }, []);

  // Get the appropriate loading indicator component for a message
  const getLoadingIndicator = useCallback((messageId, darkMode, message) => {
    const status = getMessageStatus(messageId);
    
    // 🧠 FALLBACK: If no status but message has thinking data, create status from message
    if ((!status.type || status.type === STATUS_TYPES.COMPLETE) && message?.thinkingContent && message?.thinkingDuration) {
      const fallbackStatus = {
        type: STATUS_TYPES.CLAUDE_THINKING,
        data: {
          isThinking: false,
          thinkingContent: message.thinkingContent,
          duration: message.thinkingDuration
        }
      };
      
      // Update the status map with the fallback
      updateMessageStatus(messageId, fallbackStatus);
      
      return {
        component: 'ThinkingIndicator',
        props: fallbackStatus.data
      };
    }
    

    
    if (!status.type || status.type === STATUS_TYPES.COMPLETE) {
      return null;
    }
    
    switch (status.type) {
      case STATUS_TYPES.THINKING:
        return {
          component: 'DefaultLoading',
          props: { dots: true, darkMode }
        };
        
      case STATUS_TYPES.CLAUDE_THINKING:
        return {
          component: 'ThinkingIndicator',
          props: {
            isThinking: status.data.isThinking || false,
            thinkingContent: status.data.thinkingContent || '',
            duration: status.data.duration || 0
          }
        };
        
      // SEARCHING is handled directly by ChatMessage component
      // It has the proper SearchIndicator with favicon animations and dropdown
        
      case STATUS_TYPES.ANALYZING:
        return {
          component: 'DocumentAnalysisIndicator',
          props: {
            progress: status.data.progress || 0,
            message: status.data.message || 'Analyzing document...'
          }
        };
        
      case STATUS_TYPES.DEEP_RESEARCH:
        return {
          component: 'DeepResearchIndicator',
          props: {
            stage: status.data.stage,
            progress: status.data.progress,
            message: status.data.message
          }
        };
        
      case STATUS_TYPES.GENERATING_IMAGE:
        return {
          component: 'ImageGenerationSkeleton',
          props: {
            optimalSize: status.data.optimalSize || 'auto',
            darkMode
          }
        };
      
      case STATUS_TYPES.GENERATING_FILE:
        return {
          component: 'FileGenerationIndicator',
          props: {
            format: status.data.format || 'file',
            darkMode
          }
        };
        
      case STATUS_TYPES.SUPERBUCKET_QUERY:
        return {
          component: 'SuperBucketIndicator',
          props: {
            darkMode
          }
        };
        
      case STATUS_TYPES.COMPLETE:
        // Message is complete - no loading indicator needed
        return null;
        
      default:
        return null;
    }
  }, [getMessageStatus, updateMessageStatus, STATUS_TYPES]);

  // Helper to determine if message should show loading state
  const isMessageLoading = useCallback((messageId) => {
    const status = getMessageStatus(messageId);
    return status.type && status.type !== STATUS_TYPES.COMPLETE;
  }, [getMessageStatus, STATUS_TYPES]);

  const contextValue = {
    STATUS_TYPES,
    updateMessageStatus,
    getMessageStatus,
    clearMessageStatus,
    getLoadingIndicator,
    isMessageLoading
  };

  return (
    <MessageStatusContext.Provider value={contextValue}>
      {children}
    </MessageStatusContext.Provider>
  );
};

/**
 * Hook to use the shared message status context
 * Now all components using this hook will share the same status state
 */
export const useMessageStatus = () => {
  const context = useContext(MessageStatusContext);
  if (!context) {
    throw new Error('useMessageStatus must be used within a MessageStatusProvider');
  }
  return context;
}; 